import { createContext, type ComponentType, useContext } from "react";

/**
 * What the app knows about the signed-in user, independent of Clerk. With auth off the local
 * provider supplies a stub; with it on, `clerk/provider.tsx` maps Clerk's state onto this.
 */
export interface AuthUser {
  id: string;
  /** Full name, else the email, else the id (what the API's `display_name` falls back to). */
  name: string;
  email: string | null;
}

export type AuthStatus =
  /** Clerk has not reported yet (or is switching the active organization). */
  | "loading"
  /** No session: the gate shows sign-in. */
  | "signed-out"
  /** Signed in, but no organization could be activated: the gate shows "no access". */
  | "no-org"
  /** Signed in with the organization active (or auth off). */
  | "ready";

export interface AuthState {
  /** `VITE_AUTH_ENABLED`; false means nothing here matters and the gate renders through. */
  enabled: boolean;
  status: AuthStatus;
  user: AuthUser | null;
  /** Active Clerk organization; null in local mode or before activation. */
  organizationId: string | null;
  /** The user's organization memberships, once known (null while unknown or when auth is off). */
  memberships: number | null;
  /** Clerk's user button for the header (null when auth is off). */
  UserButton: ComponentType | null;
  /** Clerk's organization switcher for the header (null when auth is off). */
  OrganizationSwitcher: ComponentType | null;
  /** End the session, then go to `redirectUrl` (the sign-in screen by default). */
  signOut(options?: { redirectUrl?: string }): Promise<void>;
  /** Re-read the memberships (the no-access screen polls for an accepted invite). */
  recheck(): Promise<void>;
}

/** The auth-off state: a local user, always ready. */
export const LOCAL_AUTH: AuthState = {
  enabled: false,
  status: "ready",
  user: { id: "local", name: "Local user", email: null },
  organizationId: null,
  memberships: null,
  UserButton: null,
  OrganizationSwitcher: null,
  signOut: () => Promise.resolve(),
  recheck: () => Promise.resolve(),
};

export const AuthContext = createContext<AuthState>(LOCAL_AUTH);

/** The auth state (the local stub outside any provider). */
export function useAuthState(): AuthState {
  return useContext(AuthContext);
}

/** True when the Clerk gate is on (cards show "uploaded by", the header shows the user button). */
export function useAuthEnabled(): boolean {
  return useContext(AuthContext).enabled;
}
